import { useCallback, useEffect, useState } from 'react'
import { ApiError, DEVICE_IDENTITY_RENEWED_CODE, clearAdminToken, getAdminAuthMethod, getAdminToken, getAuthOptions, loadAppData, loginWithAdminPhone, logoutAdmin, sendAdminPhoneCode, setAdminToken } from './api'
import { Icon } from './components/Icon'
import { Modal } from './components/UI'
import { Sidebar, type PageKey } from './components/Sidebar'
import { TopBar } from './components/TopBar'
import { DashboardPage } from './pages/DashboardPage'
import { NodesPage } from './pages/NodesPage'
import { ProvidersPage } from './pages/ProvidersPage'
import { ServicesPage } from './pages/ServicesPage'
import { DomainsPage } from './pages/DomainsPage'
import { TerminalPage } from './pages/TerminalPage'
import { DocsPage } from './pages/DocsPage'
import type { AppData, AuthOptions } from './types'

const emptyData: AppData = {
  overview: null, nodes: [], providers: [], routes: [], commands: [], domains: [], catalog: [], apiKeys: [], providerTypes: [], audits: [], meta: null, installCommand: '', tianNAS: null,
}

const defaultAuthOptions: AuthOptions = {
  version: '0.4.0',
  phone_enabled: true,
  token_enabled: true,
  preferred: 'phone',
}

export default function App() {
  const [page, setPage] = useState<PageKey>(() => (localStorage.getItem('resourcehub-page') as PageKey) || 'dashboard')
  const [data, setData] = useState<AppData>(emptyData)
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState<boolean | null>(null)
  const [authOptions, setAuthOptions] = useState<AuthOptions>(defaultAuthOptions)
  const [connectionError, setConnectionError] = useState('')
  const [mobileNav, setMobileNav] = useState(false)
  const [tokenModal, setTokenModal] = useState(false)
  const [tokenInput, setTokenInput] = useState(getAdminToken())
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null)

  const notify = useCallback((message: string, tone: 'success' | 'error' = 'success') => {
    setToast({ message, tone })
    window.setTimeout(() => setToast(null), 3600)
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const next = await loadAppData()
      setData(next)
      setAuthenticated(true)
      setConnectionError('')
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setAuthenticated(false)
        setConnectionError('')
      } else {
        setConnectionError(error instanceof Error ? error.message : '无法连接 TianCloud API')
      }
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true
    getAuthOptions().then((options) => {
      if (active) setAuthOptions(options)
    }).catch(() => undefined)
    reload().catch(() => undefined)
    return () => { active = false }
  }, [reload])

  useEffect(() => {
    if (!authenticated) return
    const timer = window.setInterval(() => loadAppData().then(setData).catch(() => undefined), 15_000)
    return () => window.clearInterval(timer)
  }, [authenticated])

  const navigate = (next: PageKey) => {
    setPage(next)
    localStorage.setItem('resourcehub-page', next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const saveToken = async () => {
    setAdminToken(tokenInput)
    try {
      await reload()
      setTokenModal(false)
      notify('管理授权码已验证', 'success')
    } catch (error) {
      setAuthenticated(false)
      throw error
    }
  }

  const saveTokenFromModal = () => {
    saveToken().catch((error) => notify(error instanceof Error ? error.message : '授权码验证失败', 'error'))
  }

  const signOut = async () => {
    let remoteError = ''
    if (data.meta?.tiantian_nas?.enabled && getAdminAuthMethod() === 'phone') {
      try {
        await logoutAdmin()
      } catch (error) {
        remoteError = error instanceof Error ? error.message : '远程会话撤销失败'
      }
    }
    clearAdminToken()
    setTokenModal(false)
    setAuthenticated(false)
    setData(emptyData)
    if (remoteError) notify(`已退出当前浏览器；${remoteError}`, 'error')
  }

  const phoneSession = data.meta?.tiantian_nas?.enabled && getAdminAuthMethod() === 'phone'

  if (authenticated === null && loading) return <LoadingScreen />
  if (authenticated === false) return <LoginScreen authOptions={authOptions} onAuthenticated={reload} initialError={connectionError} legacyToken={tokenInput} setLegacyToken={setTokenInput} onLegacyLogin={saveToken} />
  if (connectionError && !data.meta) return <ConnectionScreen message={connectionError} onRetry={() => reload().catch(() => undefined)} />

  return <div className="app-shell">
    <Sidebar page={page} onChange={navigate} open={mobileNav} onClose={() => setMobileNav(false)} />
    <div className="main-shell">
      <TopBar page={page} loading={loading} meta={data.meta} onRefresh={() => reload().catch((error) => notify(error instanceof Error ? error.message : '刷新失败', 'error'))} onMenu={() => setMobileNav(true)} onToken={() => { setTokenInput(getAdminToken()); setTokenModal(true) }} />
      {connectionError && <div className="connection-banner"><Icon name="alert" /><span>后台自动刷新失败：{connectionError}</span><button onClick={() => reload().catch(() => undefined)}>重试</button></div>}
      <main className="page-content">
        {page === 'dashboard' && <DashboardPage data={data} onNavigate={navigate} />}
        {page === 'nodes' && <NodesPage data={data} reload={reload} notify={notify} onNavigate={navigate} />}
        {page === 'resources' && <ProvidersPage data={data} reload={reload} notify={notify} />}
        {page === 'services' && <ServicesPage data={data} reload={reload} notify={notify} />}
        {page === 'domains' && <DomainsPage data={data} reload={reload} notify={notify} />}
        {page === 'terminal' && <TerminalPage data={data} reload={reload} notify={notify} />}
        {page === 'docs' && <DocsPage data={data} notify={notify} />}
      </main>
      <footer className="app-footer"><span>天天面板 · TianCloud v{data.meta?.version || '0.4.0'}</span><span>节点心跳 · 配额硬停 · 加密密钥 · 任务审计</span></footer>
    </div>

    <Modal open={tokenModal} title={phoneSession ? '管理员会话' : '管理授权码'} onClose={() => setTokenModal(false)}>
      {phoneSession ? <><div className="guardrail-note compact"><Icon name="lock" /><div><strong>{data.tianNAS?.user?.nickname || '天天 NAS 管理员'}</strong><p>{maskPhone(data.tianNAS?.user?.phoneNumber || '')} · 手机会话与授权码均可访问控制面。</p></div></div><div className="modal-actions"><button className="button button-ghost" onClick={() => setTokenModal(false)}>取消</button><button className="button button-primary" onClick={signOut}>安全退出</button></div></> : <><label className="field-block"><span>RESOURCEHUB_ADMIN_TOKEN</span><input type="password" value={tokenInput} onChange={(event) => setTokenInput(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && saveTokenFromModal()} /></label><div className="guardrail-note compact"><Icon name="lock" /><div><strong>授权码只存于当前浏览器</strong><p>生产环境建议再在 Cloudflare Access、VPN 或反向代理层增加身份认证。</p></div></div><div className="modal-actions"><button className="button button-ghost" onClick={() => setTokenModal(false)}>取消</button><button className="button button-primary" onClick={saveTokenFromModal}>保存并验证</button></div></>}
    </Modal>

    {toast && <div className={`toast toast-${toast.tone}`}><Icon name={toast.tone === 'success' ? 'check' : 'alert'} /><span>{toast.message}</span><button onClick={() => setToast(null)}><Icon name="x" size={16} /></button></div>}
  </div>
}

function LoadingScreen() {
  return <div className="loading-screen"><div className="loading-mark"><span /><span /><span /></div><strong>天天面板</strong><p>正在连接 TianCloud 控制平面并同步资源态势…</p><div className="loading-bar"><span /></div></div>
}

function preferredLoginMode(options: AuthOptions): 'phone' | 'token' {
  if (options.preferred === 'phone' && options.phone_enabled) return 'phone'
  if (options.preferred === 'token' && options.token_enabled) return 'token'
  if (options.phone_enabled) return 'phone'
  return 'token'
}

function LoginScreen({ authOptions, onAuthenticated, initialError, legacyToken, setLegacyToken, onLegacyLogin }: { authOptions: AuthOptions; onAuthenticated: () => Promise<void>; initialError: string; legacyToken: string; setLegacyToken: (value: string) => void; onLegacyLogin: () => Promise<void> }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState<'send' | 'login' | ''>('')
  const [error, setError] = useState(initialError)
  const [mode, setMode] = useState<'phone' | 'token'>(() => preferredLoginMode(authOptions))
  const [cooldown, setCooldown] = useState(0)
  const normalizedPhone = phone.replace(/\D/g, '').slice(-11)
  const methodAvailable = mode === 'phone' ? authOptions.phone_enabled : authOptions.token_enabled

  useEffect(() => {
    if ((mode === 'phone' && !authOptions.phone_enabled) || (mode === 'token' && !authOptions.token_enabled)) {
      setMode(preferredLoginMode(authOptions))
      setError('')
    }
  }, [authOptions, mode])

  useEffect(() => {
    if (initialError) setError(initialError)
  }, [initialError])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [cooldown])

  const sendCode = async () => {
    if (!authOptions.phone_enabled) {
      setError('当前控制面尚未配置短信验证码服务，请使用授权码登录。')
      return
    }
    setBusy('send')
    setError('')
    try {
      await sendAdminPhoneCode(normalizedPhone)
      setCooldown(60)
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码发送失败')
    } finally {
      setBusy('')
    }
  }

  const login = async () => {
    setBusy('login')
    setError('')
    try {
      if (mode === 'phone') {
        if (!authOptions.phone_enabled) throw new ApiError('当前控制面尚未配置短信验证码服务')
        await loginWithAdminPhone(normalizedPhone, code)
        await onAuthenticated()
      } else {
        if (!authOptions.token_enabled) throw new ApiError('当前控制面尚未配置管理员授权码')
        await onLegacyLogin()
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === DEVICE_IDENTITY_RENEWED_CODE) {
        setCode('')
        setCooldown(0)
      }
      setError(err instanceof Error ? err.message : '登录失败')
    } finally {
      setBusy('')
    }
  }

  const switchMode = (next: 'phone' | 'token') => {
    const available = next === 'phone' ? authOptions.phone_enabled : authOptions.token_enabled
    if (!available) return
    setMode(next)
    setError('')
  }

  return <div className="cf-login-shell">
    <section className="cf-login-pane">
      <div className="cf-login-brand"><div className="brand-mark" aria-hidden="true"><span /><span /><span /></div><div><strong>天天面板</strong><small>TIANCLOUD</small></div></div>
      <div className="cf-login-language">简体中文 <Icon name="chevron" size={14} /></div>
      <div className="cf-login-form-wrap">
        <div className="cf-login-heading"><span>TIANCLOUD CONTROL PLANE</span><h1>登录天天面板</h1><p>使用管理员手机验证码进入；授权码登录继续保留给 Agent、自动化脚本与应急维护。</p></div>
        <div className="cf-login-tabs" role="tablist" aria-label="登录方式">
          <button className={mode === 'phone' ? 'active' : ''} disabled={!authOptions.phone_enabled} title={authOptions.phone_enabled ? '使用管理员手机验证码登录' : '尚未配置短信验证码服务'} onClick={() => switchMode('phone')} role="tab" aria-selected={mode === 'phone'}><Icon name="lock" size={17} />手机验证码</button>
          <button className={mode === 'token' ? 'active' : ''} disabled={!authOptions.token_enabled} title={authOptions.token_enabled ? '使用控制面管理员授权码登录' : '尚未配置管理员授权码'} onClick={() => switchMode('token')} role="tab" aria-selected={mode === 'token'}><Icon name="key" size={17} />授权码</button>
        </div>
        {!authOptions.phone_enabled && <div className="cf-auth-unavailable"><Icon name="info" size={16} /><span>短信服务尚未配置，当前使用授权码登录；配置天天 NAS 地址后会自动启用。</span></div>}
        {!authOptions.token_enabled && <div className="cf-auth-unavailable"><Icon name="info" size={16} /><span>管理员授权码尚未配置，当前仅可使用手机验证码登录。</span></div>}

        {mode === 'phone' ? <div className="cf-login-fields">
          <label><span>手机号码</span><div className="cf-phone-input"><span className="cf-country-code">+86</span><input autoFocus inputMode="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="请输入管理员手机号" /></div></label>
          <label><span>短信验证码</span><div className="cf-code-row"><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} onKeyDown={(event) => event.key === 'Enter' && code.length === 6 && login()} placeholder="6 位验证码" /><button className="cf-code-button" disabled={busy !== '' || cooldown > 0 || normalizedPhone.length !== 11 || !authOptions.phone_enabled} onClick={sendCode}>{busy === 'send' ? '发送中…' : cooldown > 0 ? `${cooldown} 秒后重发` : '获取验证码'}</button></div></label>
        </div> : <div className="cf-login-fields">
          <label><span>控制面授权码</span><div className="cf-token-input"><Icon name="key" size={18} /><input autoFocus type="password" autoComplete="current-password" value={legacyToken} onChange={(event) => setLegacyToken(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && login()} placeholder="RESOURCEHUB_ADMIN_TOKEN" /></div></label>
          <div className="cf-token-note"><Icon name="shield" size={18} /><div><strong>为 Agent 与自动化保留</strong><p>授权码只保存在当前浏览器；人工日常登录建议优先使用手机验证码。</p></div></div>
        </div>}

        {error && <div className="cf-login-error"><Icon name="alert" size={17} /><span>{error}</span></div>}
        {!methodAvailable && <div className="cf-login-error"><Icon name="alert" size={17} /><span>当前登录方式尚未配置。</span></div>}
        <button className="cf-login-submit" disabled={busy !== '' || !methodAvailable || (mode === 'phone' ? normalizedPhone.length !== 11 || code.length !== 6 : legacyToken.trim().length === 0)} onClick={login}>
          {busy === 'login' ? <Icon name="refresh" className="spin" /> : <Icon name={mode === 'phone' ? 'lock' : 'key'} />}
          {busy === 'login' ? '安全验证中…' : mode === 'phone' ? '验证并登录' : '使用授权码登录'}
        </button>
        <p className="cf-login-legal">继续即表示你同意本组织的安全策略、设备验证与审计规则。</p>
      </div>
      <div className="cf-login-footer">© 2026 TianCloud · Internal Control Plane</div>
    </section>

    <aside className="cf-login-visual" aria-hidden="true">
      <div className="cf-pixel-map"><span className="pixel-cloud pixel-cloud-one" /><span className="pixel-cloud pixel-cloud-two" /><span className="pixel-cloud pixel-cloud-three" /></div>
      <div className="cf-login-visual-copy"><span>TIANCLOUD CONNECT 2026</span><h2>让每一台服务器，<br />都稳定地连接在一起。</h2><p>统一接入 · 免费额度调度 · Hub 中继 · 应用自动升级</p><div className="cf-visual-status"><span className="live-dot" />Hub 服务正常</div></div>
    </aside>
  </div>
}

function ConnectionScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="login-screen"><section className="login-card connection-card"><div className="empty-icon danger"><Icon name="network" size={30} /></div><h1>无法连接控制平面</h1><p>{message}</p><button className="button button-primary login-button" onClick={onRetry}><Icon name="refresh" />重新连接</button><small className="login-hint">开发模式请确认 Go API 正在 <code>:8080</code> 运行。</small></section></div>
}

function maskPhone(phone: string): string {
  return phone.length === 11 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : '管理员手机号'
}
